import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../../constants';

const VendorLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 4) {
      newErrors.password = 'Password must be at least 4 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validateForm()) return;

    setLoading(true);

    const loginData = {
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
    };

    console.log('🔐 Vendor login attempt with:', loginData.email);

    try {
      const response = await fetch(`${url}/vendors/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });

      console.log('📥 Response status:', response.status);
      const data = await response.json();
      console.log('📥 Response data:', data);

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // ✅ Save vendor token and data
      localStorage.setItem('vendor_token', data.access_token);
      localStorage.setItem('vendor_data', JSON.stringify(data.vendor));
      
      // ✅ Clear other role tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('admin_token');

      // ✅ Refresh vendor data from /vendors/me
      try {
        const meRes = await fetch(`${url}/vendors/me`, {
          headers: {
            Authorization: `Bearer ${data.access_token}`,
          },
        });
        if (meRes.ok) {
          const freshVendor = await meRes.json();
          localStorage.setItem('vendor_data', JSON.stringify(freshVendor));
        }
      } catch (e) {
        console.warn('Failed to refresh vendor data:', e);
      }

      console.log('✅ Vendor login successful:', data.vendor?.business_name);
      
      // Success message + redirect
      setTimeout(() => {
        navigate('/vendor/dashboard', { replace: true });
      }, 1500);

    } catch (error) {
      console.error('❌ Vendor login error:', error);
      setApiError(error.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          py: { xs: 4, md: 8 },
          background: `
            linear-gradient(135deg, #0f5328 0%, #1a7f3a 25%, #2d9e4a 50%, #4caf50 75%, #66bb6a 100%),
            radial-gradient(circle at 15% 25%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 85% 65%, rgba(255,255,255,0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 35% 15%, rgba(76,175,80,0.3) 0%, transparent 60%),
            radial-gradient(circle at 65% 85%, rgba(26,127,58,0.25) 0%, transparent 50%)
          `,
          backgroundSize: '400% 400%, 250px 250px, 350px 350px, 300px 200px, 220px 220px',
          animation: 'heroShift 6s ease infinite',
          overflow: 'hidden',
        }}
      >
        {/* Floating Shapes - IDENTICAL */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            background: 'radial-gradient(circle, rgba(76,175,80,0.5) 0%, rgba(26,127,58,0.2) 70%)',
            borderRadius: '50%',
            animation: 'float1 18s infinite linear',
            boxShadow: '0 0 40px rgba(76,175,80,0.6)',
            zIndex: 1,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '65%',
            right: '8%',
            width: 160,
            height: 160,
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 70%)',
            borderRadius: '50%',
            animation: 'float2 14s infinite linear',
            boxShadow: '0 0 30px rgba(255,255,255,0.5)',
            zIndex: 1,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            right: '15%',
            width: 140,
            height: 140,
            background: 'linear-gradient(45deg, rgba(46,158,74,0.45) 0%, rgba(102,187,106,0.3) 100%)',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            animation: 'float3 16s infinite linear',
            boxShadow: '0 0 25px rgba(46,158,74,0.5)',
            zIndex: 1,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '20%',
            width: 100,
            height: 100,
            background: 'radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(76,175,80,0.4) 70%)',
            borderRadius: '50%',
            animation: 'float4 12s infinite linear',
            boxShadow: '0 0 20px rgba(76,175,80,0.6)',
            zIndex: 1,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '45%',
            right: '25%',
            width: 180,
            height: 80,
            background: 'linear-gradient(90deg, rgba(26,127,58,0.4) 0%, rgba(46,158,74,0.5) 50%, rgba(102,187,106,0.3) 100%)',
            borderRadius: '50px',
            animation: 'float5 22s infinite linear',
            boxShadow: '0 0 35px rgba(26,127,58,0.5)',
            zIndex: 1,
          }}
        />

        <Container 
          maxWidth="sm" 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: '100%',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 4, sm: 5 }, 
              borderRadius: 3, 
              width: '100%',
              bgcolor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            {/* Header - Vendor Specific */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" gutterBottom fontWeight={700} sx={{ color: '#1a7f3a' }}>
                Vendor Portal
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Login to manage your business listings
              </Typography>
            </Box>

            {/* Error Alert */}
            {apiError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setApiError('')}>
                {apiError}
              </Alert>
            )}

            {/* Login Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                alignItems: 'center',
                width: '100%',
              }}
            >
              {/* Email Input */}
              <TextField
                label="Business Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="vendor@business.com"
                sx={{ width: '100%', maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
              />

              {/* Password Input */}
              <TextField
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                placeholder="Enter your password"
                sx={{ width: '100%', maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
              />

              {/* Login Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  fontWeight: 600, 
                  maxWidth: 400,
                  bgcolor: '#1a7f3a',
                  '&:hover': {
                    bgcolor: '#0f5328',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(26,127,58,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Logging in...
                  </Box>
                ) : (
                  'Login as Vendor'
                )}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/Vendor/Vendor-forgot-Password')}
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  maxWidth: 400,
                  color: '#1a7f3a',
                  fontWeight: 500,
                }}
                >
                Forgot Password
              </Button>
                {/* forgot password*/}
              {/* Divider */}
              <Box sx={{ textAlign: 'center', my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have a vendor account?
                </Typography>
              </Box>

              {/* Register Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => navigate('/vendor/register')}
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  fontWeight: 600, 
                  maxWidth: 400,
                  borderColor: '#1a7f3a',
                  color: '#1a7f3a',
                  '&:hover': {
                    borderColor: '#0f5328',
                    bgcolor: 'rgba(26,127,58,0.05)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Register as Vendor
              </Button>

              {/* User Login Link */}
             
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/login')}
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  maxWidth: 400,
                  color: '#1a7f3a',
                  fontWeight: 500,
                }}
              >
                Login as Customer
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* SAME ANIMATIONS */}
      <style jsx>{`
        @keyframes heroShift {
          0%, 100% { background-position: 0% 50%, 20% 30%, 85% 65%, 35% 15%, 65% 85%; }
          25% { background-position: 100% 50%, 100% 100%, 15% 100%, 85% 50%, 35% 0%; }
          50% { background-position: 50% 100%, 0% 0%, 100% 100%, 0% 50%, 100% 50%; }
          75% { background-position: 0% 50%, 0% 100%, 85% 0%, 35% 100%, 65% 50%; }
        }
        
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          25% { transform: translate(40px, -40px) rotate(90deg) scale(1.1); }
          50% { transform: translate(-20px, 30px) rotate(180deg) scale(0.95); }
          75% { transform: translate(25px, -25px) rotate(270deg) scale(1.05); }
        }
        
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-50px, 40px) rotate(-120deg); }
          66% { transform: translate(35px, -35px) rotate(-240deg); }
        }
        
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          33% { transform: translate(-30px, 25px) rotate(120deg) scale(1.15); }
          66% { transform: translate(20px, -20px) rotate(240deg) scale(0.9); }
        }
        
        @keyframes float4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-25px, 25px) rotate(180deg); }
        }
        
        @keyframes float5 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.1); }
          66% { transform: translate(-25px, 20px) scale(0.95); }
        }
      `}</style>
    </>
  );
};

export default VendorLogin;
